import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators, FormArray } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { DialogModule } from 'primeng/dialog';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { DropdownModule } from 'primeng/dropdown';
import { MultiSelectModule } from 'primeng/multiselect';
import { ChipsModule } from 'primeng/chips';
import { InputNumberModule } from 'primeng/inputnumber';
import { TooltipModule } from 'primeng/tooltip';
import { MessageService, ConfirmationService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { PackagesService } from '../../../services/payments/packages/packages.service';
import { ClipboardModule, Clipboard } from '@angular/cdk/clipboard';
import { Package } from '../../../models/payment/package.model';
import { PackageCategoryEnum, DurationTypeEnum } from '../../../models/payment/package-enums.model';
import { CreatePackageRequest, UpdatePackageRequest } from '../../../models/payment/createpackageRequests';
import { PackagesRequest } from '../../../models/payment/packagesRequest';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { Component, OnInit } from '@angular/core';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ContentEditorComponent, EditorContent } from '../../../utility/content-editor/content-editor.component';

@Component({
  selector: 'app-packages',
  standalone: true,
  imports: [
    CommonModule, ReactiveFormsModule, ButtonModule, InputTextModule, DialogModule,
    InputTextareaModule, DropdownModule, MultiSelectModule, ChipsModule, InputNumberModule,
    TooltipModule, ToastModule, AutoCompleteModule, ProgressSpinnerModule,
    ContentEditorComponent, ClipboardModule, ConfirmDialogModule
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './packages.component.html',
  styleUrl: './packages.component.scss'
})
export class PackagesComponent implements OnInit {

  displayDialog = false;
  isEditMode = false;
  packageForm!: FormGroup;
  packages: Package[] = [];
  isLoading: boolean = false;
  packageListRequest: PackagesRequest = {
    name: "",
    servicesIncluded: [""],
    duration: 0,
    amount: 0,
    description: ""
  };
  filterForm!: FormGroup;
  formSubmitted = false;
  names: any[] = [];
  stateOptions = [
    { name: 'Active', value: 'Active' },
    { name: 'Inactive', value: 'Inactive' }
  ];

  categoryOptions = [
    { label: 'Undergraduate Admissions', value: PackageCategoryEnum.UndergraduateAdmissions },
    { label: 'Postgraduate Admissions', value: PackageCategoryEnum.PostgraduateAdmissions },
    { label: 'Post Admission Services', value: PackageCategoryEnum.PostAdmissionServices },
    { label: 'Housing', value: PackageCategoryEnum.Housing },
    { label: 'Education Loan', value: PackageCategoryEnum.EducationLoan },
    { label: 'Education Counselling', value: PackageCategoryEnum.EducationCounselling },
    { label: 'Writing Services', value: PackageCategoryEnum.WritingServices }
  ];

  durationTypeOptions = [
    { label: 'Days', value: DurationTypeEnum.Days },
    { label: 'Months', value: DurationTypeEnum.Months }
  ];

  appliedFilters = {
    name: '',
    packageState: ''
  };

  getCategoryNames(values: number[]): string {
    if (!values || !values.length) return '';
    return values.map(v => {
      const category = this.categoryOptions.find(c => c.value === v);
      return category ? category.label : '';
    }).filter(label => label !== '').join(', ');
  }

  columns = [
    { field: 'name', header: 'Package Name', visible: true },
    { field: 'description', header: 'Description', visible: true },
    { field: 'duration', header: 'Duration', visible: true },
    { field: 'amount', header: 'Amount', visible: true },
    { field: 'status', header: 'Status', visible: true }
  ];

  constructor(
    private packagesService: PackagesService,
    private messageService: MessageService,
    private formBuilder: FormBuilder,
    private clipboard: Clipboard,
    private confirmationService: ConfirmationService
  ) { }

  ngOnInit() {
    this.initializeForms();
    this.loadPackages();
  }

  initializeForms() {


    this.filterForm = this.formBuilder.group({
      name: [null],
      packageState: [null]
    });


    this.packageForm = this.formBuilder.group({
      id: [0],
      name: ['', [Validators.required, Validators.minLength(3)]],
      description: ['', [Validators.required, Validators.minLength(5)]],
      duration: [1, [Validators.required, Validators.min(1)]],
      categories: [[], [Validators.required, Validators.minLength(1)]],
      durationType: [DurationTypeEnum.Months, [Validators.required]],
      servicesIncluded: [[], [Validators.required, Validators.minLength(1)]],
      amount: [null, [Validators.required, Validators.min(0.01)]],
      currency: ['₹'],
      status: ['Active'],
      isReadOnly: [false]
    });
  }

  loadPackages() {
    this.isLoading = true;
    this.packagesService.getAllPackages(this.packageListRequest).subscribe({
      next: (data) => {
        const arr = Array.isArray(data) ? data :
          (data as any)?.result || (data as any)?.value || (data as any)?.data || [];
        this.packages = arr.map((item: any) => this.normalizePackage(item));
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Error loading packages:', err);
        this.packages = [];
        this.showError('Failed to load packages');
        this.isLoading = false;
      }
    });
  }

  filterNames(event: any) {
    const query = event.query;
    if (query && query.length >= 3) {
      this.packagesService.searchPackageNames(query).subscribe({
        next: (response: any) => {
          const data = response?.result || response?.value || response?.data || response;
          if (Array.isArray(data)) {
            this.names = data.map((item: any) => {
              if (typeof item === 'string') return { name: item };
              return { name: item.name || item.Name || item };
            });
          } else {
            this.names = [];
          }
        },
        error: (err) => {
          console.error('Error searching names:', err);
          this.names = [];
        }
      });
    } else {
      this.names = [];
    }
  }

  isSearchLoading = false;

  onSearch() {
    this.isSearchLoading = true;
    setTimeout(() => {
      const Name = this.filterForm.get('name')?.value;
      this.appliedFilters = {
        name: typeof Name === 'object' && Name !== null ? (Name.name || '') : (Name || ''),
        packageState: this.filterForm.get('packageState')?.value || ''
      };
      this.isSearchLoading = false;
    }, 1000);
  }

  filteredPackages(): Package[] {
    const nameFilter = (this.appliedFilters.name || '').toLowerCase();
    const statusFilter = this.appliedFilters.packageState;

    return this.packages.filter(pkg => {
      const matchesName = !nameFilter || pkg.name.toLowerCase().includes(nameFilter);
      const matchesStatus = !statusFilter || pkg.status === statusFilter;

      return matchesName && matchesStatus;
    });
  }

  addPackage() {
    this.isEditMode = false;
    this.packageForm.reset({
      id: 0,
      name: '',
      description: '',
      duration: 1,
      categories: [],
      durationType: DurationTypeEnum.Months,
      servicesIncluded: [],
      amount: null,
      currency: '₹',
      status: 'Active'
    });
    this.displayDialog = true;
  }

  editPackage(pkg: Package) {
    this.isEditMode = true;
    this.packagesService.getPackageById(pkg.id).subscribe({
      next: (response: any) => {
        let data = response;
        if (response?.result?.[0]) data = response.result[0];
        else if (response?.result && !Array.isArray(response.result)) data = response.result;
        else if (response?.value) data = response.value;
        else if (response?.data) data = response.data;

        const normalized = this.normalizePackage(data);
        
        // Map unmapped enum 0 values to null to enforce validation, and pass array for categories
        const formPayload = {
          ...normalized,
          categories: (normalized.categories && normalized.categories.length > 0) ? normalized.categories : [],
          durationType: normalized.durationType === 0 ? null : normalized.durationType
        };

        this.packageForm.patchValue(formPayload);

        // Explicitly trigger validation UI for the empty missing fields
        if (!formPayload.categories || formPayload.categories.length === 0) {
          this.packageForm.get('categories')?.markAsTouched();
          this.packageForm.get('categories')?.markAsDirty();
        }
        if (formPayload.durationType === null) {
          this.packageForm.get('durationType')?.markAsTouched();
          this.packageForm.get('durationType')?.markAsDirty();
        }

        this.displayDialog = true;
      },
      error: (err) => {
        this.showError('Failed to fetch package details');
        console.error('Error fetching package:', err);
      }
    });
  }

  toggleStatus(pkg: Package) {
    this.packagesService.updatePackageStatus(pkg.id).subscribe({
      next: () => {
        pkg.status = pkg.status === 'Active' ? 'Inactive' : 'Active';
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: `Package "${pkg.name}" is now ${pkg.status}`
        });
      },
      error: () => this.showError('Failed to update package status')
    });
  }

  copyPaymentLink(pkg: Package) {
    const baseUrl = window.location.origin;
    const paymentLink = `${baseUrl}/shop-billing?packageId=${pkg.id}`;
    
    const success = this.clipboard.copy(paymentLink);
    
    if (success) {
      this.messageService.add({
        severity: 'success',
        summary: 'Link Copied',
        detail: 'Payment link has been copied to clipboard',
        life: 3000
      });
    } else {
      this.showError('Failed to copy link to clipboard');
    }
  }

  onDescriptionChange(event: EditorContent) {
    this.packageForm.patchValue({
      description: event.htmlValue
    });
    this.packageForm.markAsDirty();
  }

  isFormValid(): boolean {
    return this.packageForm.valid;
  }

  savePackage() {
    this.formSubmitted = true;

    if (!this.packageForm.valid) {
      this.showError('Please fill in all required fields correctly');
      this.markFormGroupTouched(this.packageForm);
      return;
    }

    const formValue = this.packageForm.value;
    const payload: CreatePackageRequest = {
      name: formValue.name.trim(),
      description: formValue.description.trim(),
      duration: Number(formValue.duration),
      categories: formValue.categories,
      durationType: Number(formValue.durationType),
      amount: Number(formValue.amount),
      servicesIncluded: formValue.servicesIncluded
    };

    const request = this.isEditMode
      ? this.packagesService.updatePackage(formValue.id, { ...payload, id: formValue.id } as UpdatePackageRequest)
      : this.packagesService.createPackage(payload as CreatePackageRequest);

    request.subscribe({
      next: (response) => this.handleSaveSuccess(response),
      error: (err) => this.handleSaveError(err)
    });
  }

  cancelDialog() {
    this.displayDialog = false;
    this.formSubmitted = false;
    this.packageForm.reset();
  }

  private markFormGroupTouched(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();

      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  private handleSaveSuccess(response: any) {
    let data = response;

    if (response?.result?.[0]) data = response.result[0];
    else if (response?.value) data = response.value;
    else if (response?.data) data = response.data;

    if (!data?.id && !data?.Id) {
      this.showError('Package saved but could not refresh the list');
      this.loadPackages();
      this.displayDialog = false;
      return;
    }

    const normalized = this.normalizePackage(data);

    if (this.isEditMode) {
      const idx = this.packages.findIndex(p => p.id === normalized.id);
      if (idx !== -1) this.packages[idx] = normalized;
      else this.loadPackages();
    } else {
      this.packages.push(normalized);
    }

    this.displayDialog = false;
    this.formSubmitted = false;
    this.messageService.add({
      severity: 'success',
      summary: 'Success',
      detail: `Package ${this.isEditMode ? 'updated' : 'created'} successfully`
    });
  }

  private handleSaveError(err: any) {
    let detail = `Failed to ${this.isEditMode ? 'update' : 'create'} package`;

    if (err.status === 500) {
      detail = 'Server Error (500). Please ensure this Package Name is UNIQUE.';
    } else if (err.error?.errors) {
      detail = Object.values(err.error.errors).flat().join('. ');
    } else if (err.error?.message) {
      detail = err.error.message;
    }

    this.messageService.add({
      severity: 'error',
      summary: 'API Error',
      detail,
      life: 5000
    });
  }

  private normalizePackage(data: any): Package {
    const isActive = data.status === 0 || data.Status === 0 ||
      data.Status === 'Active' || data.status === 'Active';

    let servicesIncluded = data.ServicesIncluded ?? data.servicesIncluded ??
      data.services_included ?? data.services ?? [];

    if (!Array.isArray(servicesIncluded)) servicesIncluded = [];

    return {
      id: data.Id ?? data.id ?? 0,
      name: data.Name ?? data.name ?? '',
      description: data.Description ?? data.description ?? '',
      status: isActive ? 'Active' : 'Inactive',
      duration: data.Duration ?? data.duration ?? 0,
      categories: data.Categories ?? data.categories ?? [],
      durationType: data.DurationType ?? data.durationType ?? 0,
      servicesIncluded: servicesIncluded,
      amount: data.Amount ?? data.amount ?? 0,
      currency: data.Currency ?? data.currency ?? '₹',
      isReadOnly: data.IsReadOnly ?? data.isReadOnly ?? false
    };
  }

  private showError(detail: string) {
    this.messageService.add({ severity: 'error', summary: 'Error', detail });
  }

  get name() { return this.packageForm.get('name'); }
  get description() { return this.packageForm.get('description'); }
  get duration() { return this.packageForm.get('duration'); }
  get categories() { return this.packageForm.get('categories'); }
  get durationType() { return this.packageForm.get('durationType'); }
  get servicesIncluded() { return this.packageForm.get('servicesIncluded'); }
  get amount() { return this.packageForm.get('amount'); }

  deletePackage(pkg: Package) {
  this.confirmationService.confirm({
    message: `Are you sure you want to delete the package "${pkg.name}"?`,
    header: 'Delete Confirmation',
    icon: 'pi pi-exclamation-triangle',
    acceptLabel: 'Yes',
    acceptIcon: 'pi pi-check',
    rejectLabel: 'No',
    rejectIcon: 'pi pi-times',
    acceptButtonStyleClass: 'p-button-primary accept-delete-btn',
    rejectButtonStyleClass: 'p-button-secondary reject-delete-btn',
    accept: () => {
      this.packagesService.deletePackage(pkg.id).subscribe({
        next: () => this.reloadAfterDelete(pkg),
        error: (err) => {
          const detail = err?.error?.message
            || (err?.error?.errors ? Object.values(err.error.errors).flat().join('. ') : null)
            || 'Failed to delete package';
          this.messageService.add({ severity: 'error', summary: 'Cannot Delete', detail, life: 6000 });
        }
      });
    }
  });
}

  private reloadAfterDelete(pkg: Package) {
    this.isLoading = true;
    this.packagesService.getAllPackages(this.packageListRequest).subscribe({
      next: (data) => {
        const arr = Array.isArray(data) ? data :
          (data as any)?.result || (data as any)?.value || (data as any)?.data || [];
        this.packages = arr.map((item: any) => this.normalizePackage(item));
        this.isLoading = false;

        const stillExists = this.packages.some(p => p.id === pkg.id);

        if (stillExists) {
          // Backend kept the package because it has transactions (marked Inactive instead)
          this.messageService.add({
            severity: 'warn',
            summary: 'Package Cannot Be Deleted',
            detail: `"${pkg.name}" cannot be deleted because it has existing transactions. It has been marked as Inactive instead.`,
            life: 6000
          });
        } else {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: `Package "${pkg.name}" deleted successfully`
          });
        }
      },
      error: () => {
        this.isLoading = false;
        this.showError('Could not refresh the package list. Please reload the page.');
      }
    });
  }
}
